import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../../components/BackButton';
import { FormPageShell } from '../../components/DataTablePanel';
import { FinancialLinesEditor } from '../../components/FinancialLinesEditor';
import {
  Button,
  Callout,
  Card,
  DateInput,
  Label,
  LoadingSpinner,
  SaveIcon,
  Select,
  TextInput,
  Textarea,
  XIcon,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { InvoiceCategory } from '../../types/invoice';
import {
  EditableFinancialLine,
  amountToApiString,
  createEmptyLine,
  parseAmountInput,
} from '../../utils/financialLines';

type FormState = {
  customerId: string;
  categoryId: string;
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string;
  notes: string;
  lines: EditableFinancialLine[];
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function emptyForm(): FormState {
  return {
    customerId: '',
    categoryId: '',
    invoiceNo: '',
    invoiceDate: todayIso(),
    dueDate: todayIso(),
    notes: '',
    lines: [createEmptyLine('INVOICE')],
  };
}

export function InvoiceFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const labels = t().invoices;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [categories, setCategories] = useState<InvoiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      try {
        const [customerResult, categoryResult] = await Promise.all([
          api.getCustomerLookup({ active: 'true' }),
          api.getInvoiceCategories(true),
        ]);
        if (cancelled) return;
        setCustomers(customerResult);
        setCategories(categoryResult);

        if (isEdit && id) {
          const invoice = await api.getInvoice(Number(id));
          if (cancelled) return;
          if (
            !customerResult.some(
              (customer) => customer.id === invoice.customer.id,
            )
          ) {
            setCustomers([
              ...customerResult,
              {
                id: invoice.customer.id,
                name: invoice.customer.name,
                active: true,
                creditDays: 0,
              },
            ]);
          }
          setForm({
            customerId: String(invoice.customer.id),
            categoryId: invoice.category ? String(invoice.category.id) : '',
            invoiceNo: invoice.invoiceNo,
            invoiceDate: invoice.invoiceDate.slice(0, 10),
            dueDate: invoice.dueDate.slice(0, 10),
            notes: invoice.notes ?? '',
            lines: invoice.lines.map((line) => ({
              key: `line-${line.id}`,
              type: line.type,
              amount: line.amount,
              description: line.description ?? '',
            })),
          });
          // ensure inactive category still appears in select
          if (
            invoice.category &&
            !categoryResult.some((c) => c.id === invoice.category!.id)
          ) {
            setCategories((prev) => [...prev, invoice.category!]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCustomerChange(customerId: string) {
    setInfo(null);
    if (!customerId) {
      updateField('customerId', '');
      return;
    }

    const customer = customers.find((c) => String(c.id) === customerId);
    const creditDays = customer?.creditDays ?? 0;

    setForm((prev) => ({
      ...prev,
      customerId,
      ...(isEdit
        ? {}
        : { dueDate: addDaysIso(prev.invoiceDate, creditDays) }),
    }));

    if (isEdit) return;

    try {
      const agreement = await api.getCustomerAgreement(Number(customerId));
      if (!agreement || agreement.lines.length === 0) {
        setInfo(labels.noAgreementToCopy);
        return;
      }
      updateField(
        'lines',
        agreement.lines.map((line) => ({
          key: `agr-${line.id}-${Math.random().toString(36).slice(2, 6)}`,
          type: line.type,
          amount: line.amount,
          description: line.description ?? '',
        })),
      );
      setInfo(labels.agreementCopied);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  function handleInvoiceDateChange(invoiceDate: string) {
    const customer = customers.find((c) => String(c.id) === form.customerId);
    const creditDays = customer?.creditDays ?? 0;
    setForm((prev) => ({
      ...prev,
      invoiceDate,
      dueDate:
        !isEdit && customer
          ? addDaysIso(invoiceDate, creditDays)
          : prev.dueDate,
    }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.customerId) {
      setError(labels.requiredCustomer);
      return;
    }
    if (!form.invoiceNo.trim()) {
      setError(labels.requiredInvoiceNo);
      return;
    }
    if (!form.invoiceDate) {
      setError(labels.requiredInvoiceDate);
      return;
    }
    if (!form.dueDate) {
      setError(labels.requiredDueDate);
      return;
    }

    const validLines = form.lines.filter(
      (line) => line.amount.trim() !== '' || line.description.trim() !== '',
    );
    if (validLines.length === 0) {
      setError(labels.requiredLines);
      return;
    }
    for (const line of validLines) {
      const amount = parseAmountInput(line.amount);
      if (!line.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
        setError(t().common.amountMustBePositive);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        customerId: Number(form.customerId),
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        invoiceNo: form.invoiceNo.trim(),
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate,
        notes: form.notes.trim() || undefined,
        lines: validLines.map((line, index) => ({
          type: line.type,
          amount: amountToApiString(line.amount),
          description: line.description.trim() || undefined,
          sortOrder: index,
        })),
      };

      const saved = isEdit
        ? await api.updateInvoice(Number(id), payload)
        : await api.createInvoice(payload);

      navigate(`/invoices/${saved.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} size="small" />;
  }

  const customerOptions = customers.map((customer) => ({
    value: String(customer.id),
    label: customer.name,
  }));

  const categoryOptions = categories.map((category) => ({
    value: String(category.id),
    label: `${category.name}${!category.active ? t().common.inactiveSuffix : ''}`,
  }));

  return (
    <FormPageShell
      onSubmit={handleSubmit}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(isEdit ? `/invoices/${id}` : '/invoices')
            }
          >
            <XIcon className="h-4 w-4" />
            {t().common.cancel}
          </Button>
          <Button type="submit" loading={saving}>
            <SaveIcon className="h-4 w-4" />
            {t().common.save}
          </Button>
        </>
      }
    >
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-ink">
          {isEdit ? labels.edit : labels.new}
        </h2>
        <BackButton fallback={isEdit && id ? `/invoices/${id}` : '/invoices'} />
      </div>

      {error ? <Callout variant="danger">{error}</Callout> : null}
      {info ? <Callout variant="info">{info}</Callout> : null}

      <Card className="space-y-4 p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label required>{labels.customer}</Label>
            <Select
              value={form.customerId}
              disabled={isEdit}
              options={customerOptions}
              placeholder={labels.selectCustomer}
              showClearButton={!isEdit}
              onChange={(e) => void handleCustomerChange(e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.category}</Label>
            <Select
              value={form.categoryId}
              options={categoryOptions}
              placeholder={labels.selectCategory}
              showClearButton
              onChange={(e) => updateField('categoryId', e.target.value)}
            />
          </div>
          <div>
            <Label required>{labels.invoiceNo}</Label>
            <TextInput
              value={form.invoiceNo}
              onChange={(e) => updateField('invoiceNo', e.target.value)}
              showClearButton={false}
              placeholder=""
            />
          </div>
          <div className="hidden md:block" />
          <div className="min-w-0">
            <Label required>{labels.invoiceDate}</Label>
            <DateInput
              value={form.invoiceDate}
              onChange={(e) => handleInvoiceDateChange(e.target.value)}
              showClearButton={false}
            />
          </div>
          <div className="min-w-0">
            <Label required>{labels.dueDate}</Label>
            <DateInput
              value={form.dueDate}
              onChange={(e) => updateField('dueDate', e.target.value)}
              showClearButton={false}
            />
          </div>
        </div>
        <div>
          <Label>{labels.notes}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={2}
          />
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h3 className="text-sm font-semibold text-ink">{labels.linesSection}</h3>
        <FinancialLinesEditor
          lines={form.lines}
          onChange={(lines) => updateField('lines', lines)}
          disabled={saving}
        />
      </Card>
    </FormPageShell>
  );
}
